import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

/** Pixels past scrollTop before the content band is considered unveiled. */
const REVEAL_SCROLL_TOP_PX = 8;

interface ChapterIntroReveal {
  rootRef: RefObject<HTMLDivElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
  contentRevealed: boolean;
  scrollContentIntoView: () => void;
}

const markContentAsSeen = (
  seenRef: RefObject<boolean>,
  onContentSeenRef: RefObject<(() => void) | undefined>
) => {
  if (seenRef.current) return;
  seenRef.current = true;
  onContentSeenRef.current?.();
};

/**
 * Syncs chapter-intro content unveil with the nearest overflow scroll root —
 * used by ChapterIntroScroll for scroll/wheel reveal and once-only onContentSeen.
 *
 * `unveilQuery` is the `?unveil=1` URL flag (TOC lesson link). It seeds an
 * initial unveil; after that, scroll position alone controls blur.
 */
const useChapterIntroReveal = (
  onContentSeen?: () => void,
  unveilQuery = false
): ChapterIntroReveal => {
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const seenRef = useRef(false);
  const onContentSeenRef = useRef(onContentSeen);
  const [scrollRevealed, setScrollRevealed] = useState(unveilQuery);
  // Previous `?unveil=1` active flag. Needed because the intro stays mounted
  // when the TOC adds the query — useState only runs once.
  const [wasUnveilQueryActive, setWasUnveilQueryActive] = useState(unveilQuery);
  const unveilQueryJustActivated = unveilQuery && !wasUnveilQueryActive;
  const unveilQueryJustCleared = !unveilQuery && wasUnveilQueryActive;
  if (unveilQueryJustActivated) {
    setWasUnveilQueryActive(true);
    setScrollRevealed(true);
  } else if (unveilQueryJustCleared) {
    setWasUnveilQueryActive(false);
  }

  useEffect(() => {
    onContentSeenRef.current = onContentSeen;
  }, [onContentSeen]);

  useLayoutEffect(() => {
    const scrollRoot =
      (rootRef.current?.closest(".overflow-y-auto") as HTMLElement | null) ??
      null;
    if (!scrollRoot) return;

    // Veiled entry must start at the intro band. Leftover scroll from the
    // previous chapter Abschluss would otherwise look like an unveil.
    if (!unveilQuery) {
      scrollRoot.scrollTop = 0;
    }

    const syncReveal = () => {
      const next = scrollRoot.scrollTop > REVEAL_SCROLL_TOP_PX;
      setScrollRevealed(next);
      if (next) markContentAsSeen(seenRef, onContentSeenRef);
    };

    const onWheel = (event: WheelEvent) => {
      // When content already fits the leftover viewport there is no overflow;
      // still allow wheel to unveil / re-veil at the top.
      if (scrollRoot.scrollHeight <= scrollRoot.clientHeight + 1) {
        if (event.deltaY > 0) {
          setScrollRevealed(true);
          markContentAsSeen(seenRef, onContentSeenRef);
        } else if (event.deltaY < 0) {
          setScrollRevealed(false);
        }
      }
    };

    scrollRoot.addEventListener("scroll", syncReveal, { passive: true });
    scrollRoot.addEventListener("wheel", onWheel, { passive: true });

    return () => {
      scrollRoot.removeEventListener("scroll", syncReveal);
      scrollRoot.removeEventListener("wheel", onWheel);
    };
  }, [unveilQuery]);

  const scrollContentIntoView = () => {
    contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setScrollRevealed(true);
    markContentAsSeen(seenRef, onContentSeenRef);
  };

  return {
    rootRef,
    contentRef,
    contentRevealed: scrollRevealed,
    scrollContentIntoView,
  };
};

export { REVEAL_SCROLL_TOP_PX, useChapterIntroReveal };
